import FixedNavBar from "@/components/fixedNavBar";
import useCustomTranslation from "@/hooks/useTranslation";
import {
	dateFormat,
	east8ToEast9,
	formatNumberWithCommas,
	pxToRem,
} from "@/util/common";
import { Image, InfiniteScroll, PullToRefresh } from "antd-mobile";
import React, { forwardRef, useRef } from "react";
import { createUseStyles } from "react-jss";
import CalendarTime from "@/components/calendarTime";
import { useFinanceOrderList } from "@/hooks/useOrderRecords";
import DropTime from "@/components/dropTime";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import CustomEmpty from "@/components/Empty";
import InfiniteScrollContent from "@/components/InfiniteScrollContent";

const CalendarTimeChildComponent = forwardRef(CalendarTime);
const GameRecordsNow: React.FC = () => {
	const classes = useStyles();
	const { t } = useCustomTranslation();
	const userInfo = useSelector((state: RootState) => state.auth.userInfo);
	const childRef = useRef<any>(null);

	const params: any = {
		srcList: ["COMMISSION_OUT"], // "COMMISSION_OUT",
		uid: userInfo?.id,
	};
	const { data, targetData, hasMore, handleRefresh, handleFetchMore } =
		useFinanceOrderList(params, childRef);

	const daysToSubtract = useRef<number>(30);
	const handleOptionClick = (newIndex: number) => {
		daysToSubtract.current = newIndex;
		handleRefresh();
	};

	return (
		<div className="router-main">
			<FixedNavBar
				title={`${t("CashbackRecord")}`}
				rightContent={[
					<DropTime
						key="dropTime"
						index={daysToSubtract.current}
						handleOptionClick={handleOptionClick}
					/>,
				]}
			/>
			<div
				style={{ paddingLeft: pxToRem(20), paddingRight: pxToRem(20) }}
			>
				<CalendarTimeChildComponent
					ref={childRef}
					onSubmit={handleRefresh}
					daysToSubtract={daysToSubtract.current}
				/>
			</div>

			<div className="router-view">
				<PullToRefresh
					onRefresh={handleRefresh}
					canReleaseText={t("canReleaseText")}
					completeText={t("completeText")}
					pullingText={t("pullingText")}
					refreshingText={t("refreshingText")}
				>
					<div className={classes.columnTitle}>
						<div className="l">
							<Image
								src={require("@/assets/images/icons/pie-chart.png")}
								className="icon"
							/>
							<strong>{t("Count")}</strong>
						</div>
					</div>

					<div className={classes.countWrap}>
						<dl className={`${classes.cardMonomer} gradient`}>
							<dt>
								<Image
									src={require("@/assets/images/icons/trophies.png")}
									className="icon"
								/>
								<span style={{ color: "#8BFF78" }}>
									{t("TotalCashback")}
								</span>
							</dt>
							<dd className="u">
								{formatNumberWithCommas(
									targetData?.commission ?? 0
								)}{" "}
								USDT
							</dd>
						</dl>
					</div>

					<div className={classes.columnTitle}>
						<div className="l">
							<Image
								src={require("@/assets/images/icons/record.png")}
								className="icon"
							/>
							<strong>{t("CashbackRecord")}</strong>
						</div>
					</div>

					{data.length > 0 ? (
						<ul className={classes.listWrap}>
							{data.map((item: any) => (
								<li>
									<div className="up">
										<Image
											src={require("@/assets/images/icons/coinGaming.png")}
											className="icon"
										/>
										<p>{t("DailyCashback")}</p>
										<strong>
											{formatNumberWithCommas(item.coin)}
										</strong>
									</div>
									<div className="down">
										<span>
											{dateFormat(
												east8ToEast9(
													item.updateTime
												).getTime(),
												"YYYY-MM-dd hh:mm:ss"
											)}
										</span>
										<span>{t("Success")}</span>
									</div>
								</li>
							))}
						</ul>
					) : (
						<CustomEmpty />
					)}

					{data.length > 0 && (
						<InfiniteScroll
							loadMore={handleFetchMore}
							hasMore={hasMore}
						>
							<InfiniteScrollContent hasMore={hasMore} />
						</InfiniteScroll>
					)}
				</PullToRefresh>
			</div>
		</div>
	);
};

export default GameRecordsNow;

const useStyles = createUseStyles({
	contentWrap: {
		position: "relative",
		padding: {
			top: pxToRem(20),
			left: pxToRem(20),
			right: pxToRem(20),
		},
		"&::before": {
			position: "absolute",
			content: '""',
			width: pxToRem(164),
			height: pxToRem(138),
			left: "50%",
			top: pxToRem(20),
			transform: "translateX(-50%) scale(1.6)",
			backgroundImage: `url(${require("@/assets/images/icons/backdrop-filter.png")})`,
			backgroundSize: "100% 100%",
			objectFit: "cover",
		},
	},
	infoWrap: {
		display: "flex",
		alignItems: "center",
		paddingBottom: pxToRem(18),
		"& .icon": {
			"--size": pxToRem(48),
			"--border-radius": "100%",
			border: "1px solid #475278",
			marginRight: pxToRem(14),
		},
		"& span": {
			color: "#FFFFFF",
			fontSize: pxToRem(18),
			fontWeight: 600,
		},
	},
	totalAssetsWrap: {
		position: "relative",
		display: "flex",
		alignItems: "center",
		zIndex: 10,
		border: "1px solid #353F63",
		padding: {
			top: pxToRem(17),
			right: pxToRem(17),
			bottom: pxToRem(20),
			left: pxToRem(20),
		},
		overflow: "hidden",
		// borderImage: "linear-gradient(180deg, #2E3A61 0%, #353F63 100%) 1",
		background: "linear-gradient(156deg, #181B24 5.2%, #232C48 97.46%)",
		borderRadius: pxToRem(10),
		"& .l": {
			display: "flex",
			flexDirection: "column",
			flex: 1,
			"& p": {
				color: "#868FA8",
				fontSize: pxToRem(14),
				fontWeight: 400,
				fontFamily: "Noto Sans SC",
			},
			"& strong": {
				color: "#FFFFFF",
				fontSize: pxToRem(26),
				fontWeight: 600,
			},
		},
	},
	labelTitle: {
		color: "#FFFFFF",
		fontFamily: "Noto Sans SC",
		fontSize: pxToRem(14),
		fontWeight: 500,
		padding: {
			top: pxToRem(10),
			bottom: pxToRem(10),
		},
	},
	btn: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		width: pxToRem(112),
		height: pxToRem(40),
		fontSize: pxToRem(14),
		fontWeight: "500",
		fontFamily: "Noto Sans SC",
		"--text-color": "#FFFFFF",
		"--border-width": "0",
		background: "linear-gradient(90deg, #4675FF 0%, #0E329D 100%)",
		boxShadow:
			"0px 10px 10px 1px #184DED4D, 0px 2px 4px 0px #00C2FF40 inset",
		"& .icon": {
			width: pxToRem(22),
			height: pxToRem(18),
		},
	},
	columnTitle: {
		display: "flex",
		justifyContent: "space-between",
		padding: {
			left: pxToRem(20),
			right: pxToRem(20),
			top: pxToRem(15),
			bottom: pxToRem(15),
		},
		"& .l": {
			flex: 1,
			overflow: "hidden",
			display: "flex",
			alignItems: "center",
			"& .icon": {
				width: pxToRem(24),
				height: pxToRem(24),
				marginRight: pxToRem(8),
			},
			"& strong": {
				color: "#FFFFFF",
				fontFamily: "Noto Sans SC",
				fontSize: pxToRem(14),
				fontWeight: 500,
			},
		},
		"& .more": {
			color: "#868FA8",
			fontSize: pxToRem(14),
			fontWeight: 400,
		},
	},

	countWrap: {
		padding: {
			right: pxToRem(20),
			left: pxToRem(20),
		},
		marginBottom: pxToRem(10),
	},
	cardMonomer: {
		padding: {
			top: pxToRem(15),
			right: pxToRem(10),
			bottom: pxToRem(10),
			left: pxToRem(16),
		},
		borderRadius: pxToRem(10),
		backgroundColor: "#181A20",
		"&.gradient": {
			background: "linear-gradient(90deg, #147639 0%, #1E2846 100%)",
			"& dt": {
				color: "#FFFFFF",
			},
		},
		"&.cashback": {
			position: "relative",
			paddingLeft: pxToRem(105),
			"&:after": {
				position: "absolute",
				content: '""',
				left: pxToRem(9),
				top: pxToRem(3),
				width: pxToRem(73),
				height: pxToRem(76),
				backgroundImage: `url(${require("@/assets/images/icons/cashback.png")})`,
				backgroundRepeat: "no-repeat",
				backgroundSize: "cover",
			},
		},
		"& dt": {
			display: "flex",
			alignItems: "center",
			color: "#868FA8",
			fontSize: pxToRem(12),
			fontWeight: 400,
			"& .icon": {
				width: pxToRem(20),
				height: pxToRem(20),
				marginRight: pxToRem(4),
			},
		},
		"& dd": {
			color: "#FFFFFF",
			fontSize: pxToRem(14),
			fontWeight: 500,
			lineHeight: pxToRem(34),
			"&.u": {
				fontSize: pxToRem(18),
				fontWeight: 700,
			},
		},
	},

	listWrap: {
		"& li": {
			borderTop: "1px solid #2F3A62",
			padding: {
				top: pxToRem(16),
				left: pxToRem(20),
				right: pxToRem(22),
				bottom: pxToRem(20),
			},
			"& >div": {
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
			},
		},
		"& .up": {
			fontWeight: 500,
			marginBottom: pxToRem(3),
			"& .icon": {
				width: pxToRem(20),
				height: pxToRem(20),
				marginRight: pxToRem(6),
				border: "1px solid",
				borderImageSource:
					"linear-gradient(180deg, #4270F7 0%, #153AA8 100%)",
			},
			"& p": {
				flex: 1,
				overflow: "hidden",
				color: "#FFFFFF",
				fontSize: pxToRem(16),
			},
			"& strong": {
				color: "#8BFF78",
				fontSize: pxToRem(16),
			},
		},
		"& .down": {
			color: "#868FA8",
			fontWeight: 400,
			fontSize: pxToRem(12),
		},
	},
});
